import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import type { ShopifyBarcodeMatches } from "../../api/types";
import { Modal } from "../Modal";
import { shopifyVariantLabel } from "./shopifyVariantLabel";

function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * G-60 item 1: every unlinked Shopify size whose barcode (or SKU) equals a CRM
 * item's, proposed at once and linked with one confirmation. When several
 * items share the barcode (G-61) the person picks which one; nothing is
 * linked they did not see.
 */
export function ShopifyBarcodeMatchModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [data, setData] = useState<ShopifyBarcodeMatches | null>(null);
  const [choice, setChoice] = useState<Record<string, string | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listShopifyBarcodeMatches()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setChoice(
          Object.fromEntries(
            result.proposals.map((p) => [
              p.variant.shopifyVariantId,
              p.candidates.length === 1 ? p.candidates[0].id : null,
            ]),
          ),
        );
      })
      .catch((e) => !cancelled && setError(errText(e, "Could not load matches")));
    return () => {
      cancelled = true;
    };
  }, []);

  const pairs = Object.entries(choice).flatMap(([shopifyVariantId, itemId]) =>
    itemId ? [{ shopifyVariantId, itemId }] : [],
  );

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.setShopifyLinksBulk(pairs);
      onDone(
        result.failed.length === 0
          ? `Linked ${result.linked} Shopify ${result.linked === 1 ? "product" : "products"}.`
          : `Linked ${result.linked}; ${result.failed.length} could not be linked: ${result.failed
              .map((f) => f.message)
              .join("; ")}`,
      );
    } catch (e) {
      setError(errText(e, "Could not link"));
      setSaving(false);
    }
  };

  return (
    <Modal title="Match by barcode" onClose={onClose}>
      <div className="shopify-match">
        {error ? <div className="alert error">{error}</div> : null}
        {!data && !error ? <p className="muted">Looking for matches...</p> : null}
        {data ? (
          <>
            <p className="shopify-picker__note">
              {data.proposals.length === 0
                ? "No unlinked Shopify product shares a barcode or SKU with a CRM item."
                : `${data.proposals.length} Shopify ${
                    data.proposals.length === 1 ? "product matches" : "products match"
                  } a CRM item. Untick any that are wrong, then link them all at once.`}
            </p>
            {data.proposals.map((proposal) => {
              const id = proposal.variant.shopifyVariantId;
              const code =
                proposal.matchedBy === "barcode"
                  ? `barcode ${proposal.variant.barcode}`
                  : `SKU ${proposal.variant.sku}`;
              return (
                <div key={id} className="shopify-match__row">
                  <strong>{shopifyVariantLabel(proposal.variant)}</strong>
                  <span className="shopify-picker__note">Same {code}</span>
                  {proposal.candidates.length === 1 ? (
                    <label className="shopify-match__choice">
                      <input
                        type="checkbox"
                        checked={choice[id] != null}
                        onChange={(e) =>
                          setChoice((prev) => ({
                            ...prev,
                            [id]: e.target.checked ? proposal.candidates[0].id : null,
                          }))
                        }
                      />
                      Link to {proposal.candidates[0].name}
                    </label>
                  ) : (
                    <>
                      <span className="shopify-picker__note">
                        {proposal.candidates.length} items share it — pick the one this product is:
                      </span>
                      {proposal.candidates.map((candidate) => (
                        <label key={candidate.id} className="shopify-match__choice">
                          <input
                            type="radio"
                            name={`match-${id}`}
                            checked={choice[id] === candidate.id}
                            onChange={() => setChoice((prev) => ({ ...prev, [id]: candidate.id }))}
                          />
                          {candidate.name}
                        </label>
                      ))}
                      <label className="shopify-match__choice">
                        <input
                          type="radio"
                          name={`match-${id}`}
                          checked={choice[id] == null}
                          onChange={() => setChoice((prev) => ({ ...prev, [id]: null }))}
                        />
                        Skip for now
                      </label>
                    </>
                  )}
                </div>
              );
            })}
            {data.unmatchedVariants.length > 0 ? (
              <details>
                <summary>
                  {data.unmatchedVariants.length} unlinked Shopify{" "}
                  {data.unmatchedVariants.length === 1 ? "product has" : "products have"} no match
                </summary>
                <ul>
                  {data.unmatchedVariants.map((v) => (
                    <li key={v.shopifyVariantId}>
                      {shopifyVariantLabel(v)}
                      {v.barcode || v.sku ? "" : " (no barcode or SKU in Shopify)"}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            {data.unmatchedItems.length > 0 ? (
              <details>
                <summary>
                  {data.unmatchedItems.length} CRM {data.unmatchedItems.length === 1 ? "item" : "items"} with
                  a barcode or SKU matched no Shopify product
                </summary>
                <ul>
                  {data.unmatchedItems.map((item) => (
                    <li key={item.id}>
                      {item.name}
                      {item.barcode ? ` · ${item.barcode}` : item.sku ? ` · ${item.sku}` : ""}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
            <div className="shopify-match__footer">
              <button type="button" className="button plain" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={() => void confirm()}
                disabled={saving || pairs.length === 0}
              >
                {saving ? "Linking..." : `Link ${pairs.length}`}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
